// LoginForm — F8.7b · email + password + submit + inline errors.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { useAuthModal } from "@/context/AuthModalContext";
import { getAuthIntent, clearAuthIntent } from "@/lib/authIntent";
import { api } from "@/lib/api";

export default function LoginForm({ prefillEmail = "" }) {
    const { t } = useTranslation("landing_v2");
    const { refresh } = useAuth();
    const { closeModal, setTab } = useAuthModal();
    const navigate = useNavigate();
    const [email, setEmail] = useState(prefillEmail);
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!email || !password) return;
        setSubmitting(true);
        try {
            const res = await fetch(`/api/auth/login`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                if (data.detail === "email_not_verified") {
                    setError(t("auth.modal.error_email_not_verified"));
                } else if (data.detail === "invalid_credentials") {
                    setError(t("auth.modal.error_invalid_credentials"));
                } else {
                    setError(t("auth.modal.error_generic"));
                }
                setSubmitting(false);
                return;
            }
            // Success — flip local session flag so AuthContext.refresh() probes /me.
            try { window.localStorage.setItem("glms_session", "1"); } catch { /* noop */ }
            await refresh();
            toast.success(t("toasts.logged_in"));
            closeModal();
            // Trigger post-login intent if any (e.g. checkout).
            const intent = getAuthIntent();
            if (intent?.action === "checkout" && intent.plan) {
                clearAuthIntent();
                try {
                    const co = await api.subscriptionCheckout(intent.plan, intent.interval || "monthly");
                    if (co?.url) { window.location.href = co.url; return; }
                } catch (err) {
                    toast.error(t("auth.modal.error_generic"));
                }
                return;
            }
            // F9.7 WAVE 2I · Deep-link intent — honour ?redirect=
            if (intent?.action === "redirect_after_login" && intent.target) {
                clearAuthIntent();
                const target = intent.target;
                // Only follow same-origin paths (defense against open-redirect).
                if (typeof target === "string" && target.startsWith("/") && !target.startsWith("//")) {
                    navigate(target);
                    return;
                }
            }
                // F8.8 · Default post-login destination is /v2/dashboard (V12 shell).
                // Phase I Wave 2.5 · unification · /dashboard legacy now redirects here anyway.
                // Skip if the user is already on a private route (avoids yanking
                // people mid-flow when they log in from e.g. /marketplace/asset/xxx).
                const currentPath = window.location.pathname;
                const isPrivateRoute = /^\/(v2\/dashboard|dashboard|profile|admin|marketplace-hub|checkout)/.test(currentPath);
                if (!isPrivateRoute) {
                    navigate("/app/dashboard");
                }
        } catch {
            setError(t("auth.modal.error_generic"));
        }
        setSubmitting(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-modal-login-form">
            <div>
                <label htmlFor="login-email" className="block text-xs text-text-secondary mb-1.5">
                    {t("auth.modal.email_label")}
                </label>
                <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth.modal.email_placeholder")}
                    data-testid="auth-modal-login-email"
                    className="w-full px-3 py-2.5 rounded-xl bg-bg border border-brand-border text-text-main placeholder:text-text-secondary/60 focus:outline-none focus:border-brand-primary/60 transition-colors"
                />
            </div>
            <div>
                <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="login-password" className="block text-xs text-text-secondary">
                        {t("auth.modal.password_label")}
                    </label>
                    <button
                        type="button"
                        onClick={() => setTab("forgot")}
                        className="text-xs text-brand-primary hover:text-brand-secondary transition-colors"
                        data-testid="auth-modal-forgot-link"
                    >
                        {t("auth.modal.forgot_link")}
                    </button>
                </div>
                <input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("auth.modal.password_placeholder")}
                    data-testid="auth-modal-login-password"
                    className="w-full px-3 py-2.5 rounded-xl bg-bg border border-brand-border text-text-main placeholder:text-text-secondary/60 focus:outline-none focus:border-brand-primary/60 transition-colors"
                />
            </div>
            {error && (
                <div className="text-xs text-brand-alert" data-testid="auth-modal-login-error">
                    {error}
                </div>
            )}
            <button
                type="submit"
                disabled={submitting}
                data-testid="auth-modal-login-submit"
                className="w-full px-4 py-2.5 rounded-xl bg-white hover:bg-[#EDEDED] text-black font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {submitting ? "…" : t("auth.modal.submit_login")}
            </button>
            <div className="text-center text-xs text-text-secondary pt-1">
                <button
                    type="button"
                    onClick={() => setTab("signup")}
                    className="hover:text-text-main transition-colors"
                    data-testid="auth-modal-toggle-signup"
                >
                    {t("auth.modal.toggle_to_signup")}
                </button>
            </div>
        </form>
    );
}
